import {isFeatureEnabled} from '@github-ui/feature-flags'
import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {sanitize} from '@github-ui/sanitize'
import type {CreateRepositoryLabelMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryLabelMutation'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {
  buildConfirmedCreateLabelOperation,
  buildCreateLabelOperation,
  type LabelInput,
  labelInputFromOperation,
  LABELS_CONNECTION_PATH,
  LABELS_OPTIMISTIC_CONNECTION_FLAG,
  newTempLabelId,
} from './utils/label-connection-operations'
import {createLabelRequest} from './utils/label-requests'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseCreateLabelOptions = {
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setSubmissionErrors: (v: string) => void
  setDialogOpen: (open: boolean) => void
  onSuccess?: () => void
}

type CreateLabelHandler = (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => void

type CreateLabelContext = {
  previousData: LabelsQueryData | undefined
  tempId: string
}

/**
 * Selects the create strategy by feature flag. Both sub-hooks are always mounted so hook order is
 * stable; only the selected callback's `mutate` runs, so the flag can be flipped for a rollback.
 */
export function useCreateLabel(options: UseCreateLabelOptions): CreateLabelHandler {
  const legacy = useCreateLabelLegacy(options)
  const optimistic = useCreateLabelOptimistic(options)
  return isFeatureEnabled(LABELS_OPTIMISTIC_CONNECTION_FLAG) ? optimistic : legacy
}

function useCreateLabelOptimistic({
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setSubmissionErrors,
  setDialogOpen,
  onSuccess,
}: UseCreateLabelOptions): CreateLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useConnectionMutation<CreateRepositoryLabelMutationMutation, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    // The operation's node carries the raw input the write needs.
    mutationFn: async operation => {
      const input = labelInputFromOperation(operation)
      if (!input) throw new Error(LABELS.labelCreateError)
      return createLabelRequest(repositoryNameWithOwner, repositoryId, input)
    },
    onError: error => {
      setSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setDialogOpen(true)
    },
    onSuccess: (data, {operation}) => {
      const createdLabel = data.createLabel?.label
      const tempId = operation.type === 'addToConnection' ? operation.node.id : undefined
      if (queryKey && createdLabel && tempId) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildConfirmedCreateLabelOperation(tempId, createdLabel)) : base,
        )
      }
      onSuccess?.()
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      setDialogOpen(false)
      mutate({operation: buildCreateLabelOperation(newTempLabelId(), input)})
    },
    [mutate, setDialogOpen],
  )
}

/** The flag-off fallback: hand-rolled cache surgery with snapshot rollback. */
function useCreateLabelLegacy({
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setSubmissionErrors,
  setDialogOpen,
  onSuccess,
}: UseCreateLabelOptions): CreateLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<CreateRepositoryLabelMutationMutation, Error, LabelInput, CreateLabelContext>({
    mutationFn: (input: LabelInput) => createLabelRequest(repositoryNameWithOwner, repositoryId, input),
    onMutate: async (input): Promise<CreateLabelContext> => {
      const tempId = newTempLabelId()
      const previousData = queryKey ? queryClient.getQueryData<LabelsQueryData>(queryKey) : undefined

      // Optimistically close the dialog and insert the new label
      setDialogOpen(false)

      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            labels.totalCount = (labels.totalCount ?? 0) + 1
            labels.edges.unshift({
              node: {
                id: tempId,
                name: input.name,
                nameHTML: sanitize(input.name, {
                  ALLOWED_TAGS: [],
                  ALLOWED_ATTR: [],
                  ALLOW_ARIA_ATTR: false,
                  RETURN_TRUSTED_TYPE: false,
                }),
                color: input.color,
                description: input.description,
              },
            })
          }),
        )
      }

      return {previousData, tempId}
    },
    onError: (error, _variables, context) => {
      if (queryKey && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      setSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setDialogOpen(true)
    },
    onSuccess: (data, _variables, context) => {
      const createdLabel = data.createLabel?.label
      if (queryKey && createdLabel) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === context.tempId)
            if (edge?.node) {
              edge.node.id = createdLabel.id
              edge.node.name = createdLabel.name
              edge.node.nameHTML = createdLabel.nameHTML
              edge.node.color = createdLabel.color
              edge.node.description = createdLabel.description
            }
          }),
        )
      }
      onSuccess?.()
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(input)
    },
    [mutate],
  )
}
